import React, { useEffect, useMemo, useRef, useState } from "react";
import debounce from "debounce-promise";

import { PageArticle, Sidebar } from "./components";
import {
    findFolderPathByPageId,
    getFirstPageId,
    normalizeConfluenceHtml,
} from "./functions";
import "./styling/confluence.less";
import { fetchJson } from "./functions/fetchJson";
import useUpdateUrlParam from "../react-hooks/useUpdateUrlParam"
import useCopyToClipboard from "../react-hooks/useCopyToClipboard"

const Confluence = ({ endpoints }) => {
    const { contentTree: contentTreeLink, search: searchLink, page: pageLink } = endpoints;

    const [contentTree, setContentTree] = useState([]);
    const [treeLoading, setTreeLoading] = useState(true);
    const [treeError, setTreeError] = useState(null);

    const [searchTerm, setSearchTerm] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchError, setSearchError] = useState(null);

    const [selectedPageId, setSelectedPageId] = useState(null);
    const [selectedPage, setSelectedPage] = useState(null);
    const [pageLoading, setPageLoading] = useState(false);
    const [pageError, setPageError] = useState(null);

    const [expandedFolders, setExpandedFolders] = useState({});

    const { copied: linkCopied, copy: copyToClipboard } = useCopyToClipboard();

    const pageRequestId = useRef(0);
    const searchRequestId = useRef(0);
    const contentRef = useRef(null);

    const normalizedPageHtml = useMemo(
        () => normalizeConfluenceHtml(selectedPage?.body?.view?.value, selectedPage),
        [selectedPage]
    );

    const expandFolderPathForPage = (pageId, treeItems = contentTree) => {
        const folderPath = findFolderPathByPageId(treeItems, pageId);
        if (!folderPath?.length) {
            return;
        }
        setExpandedFolders((currentFolders) => folderPath.reduce((folders, folderId) => ({
            ...folders,
            [folderId]: true
        }), currentFolders));
    };

    const updateUrlForPage = useUpdateUrlParam("page");

    const loadPage = async (pageId, options = {}) => {
        if (!pageId || !pageLink) {
            return;
        }

        const { updateUrl = true, treeItems = contentTree } = options;
        const currentRequestId = pageRequestId.current + 1; // for race condition in async

        pageRequestId.current = currentRequestId;
        setSelectedPageId(pageId);
        setPageError(null);
        setPageLoading(true);

        if (updateUrl) {
            updateUrlForPage(pageId);
        }

        try {
            const page = await fetchJson(`${pageLink}${encodeURIComponent(pageId)}`);

            if (pageRequestId.current !== currentRequestId) {
                return;
            }

            setSelectedPage(page);
            expandFolderPathForPage(pageId, treeItems);
        } catch (error) {
            if (pageRequestId.current !== currentRequestId) {
                return;
            }

            setSelectedPage(null);
            setPageError(error.message || "Diese Seite kann nicht geladen werden.");
        } finally {
            if (pageRequestId.current === currentRequestId) {
                setPageLoading(false);
            }
        }
    };

    useEffect(() => {
        const loadContentTree = async () => {
            setTreeLoading(true);
            setTreeError(null);

            try {
                const treeResponse = await fetchJson(contentTreeLink);
                const tree = Array.isArray(treeResponse) ? treeResponse : [];
                setContentTree(tree);
                setExpandedFolders({});

                const requestedPageId = new URLSearchParams(window.location.search).get("page");
                const initialPageId = requestedPageId || getFirstPageId(tree);

                if (initialPageId) {
                    loadPage(initialPageId, {
                        updateUrl: !requestedPageId,
                        treeItems: tree
                    });
                }
            } catch (error) {
                setTreeError(error.message || "Confluence-Seiten können nicht geladen werden.");
            } finally {
                setTreeLoading(false);
            }
        };

        loadContentTree();
    }, [contentTreeLink]);

    const debouncedSearch = useMemo(
        () => debounce((term) => fetchJson(`${searchLink}${encodeURIComponent(term)}`), 250),
        [searchLink]
    );

    useEffect(() => {
        const trimmedSearch = searchTerm.trim();

        if (!trimmedSearch) {
            setSearchResults([]);
            setSearchLoading(false);
            setSearchError(null);
            return;
        }

        const currentRequestId = searchRequestId.current + 1;

        searchRequestId.current = currentRequestId;
        setSearchLoading(true);
        setSearchError(null);

        debouncedSearch(trimmedSearch)
            .then((results) => {
                if (searchRequestId.current !== currentRequestId) {
                    return;
                }

                const pageResults = (Array.isArray(results) ? results : [])
                    .filter((item) => item?.type === "page");

                setSearchResults(pageResults);
                setSearchLoading(false);
            })
            .catch((error) => {
                if (searchRequestId.current !== currentRequestId) {
                    return;
                }

                setSearchResults([]);
                setSearchError(error.message || "Die Suche in Confluence ist nicht möglich.");
                setSearchLoading(false);
            });
    }, [debouncedSearch, searchTerm]);

    const handleTreeToggle = (folderId) => {
        setExpandedFolders((currentFolders) => ({
            ...currentFolders,
            [folderId]: !currentFolders[folderId]
        }));
    };

    const handlePageSelection = (pageId) => {
        loadPage(pageId);
    };

    const handleCopyCurrentUrl = () => copyToClipboard(() => window.location.href);

    return (
        <div className="confluence-help tw-flex tw-min-h-[72vh] tw-bg-white tw-overflow-hidden tw-border tw-border-[color:var(--cf-color-border)] tw-rounded-[var(--cf-radius-md)] max-[960px]:tw-flex-col max-[960px]:tw-min-h-0">
            <Sidebar
                contentTree={contentTree}
                expandedFolders={expandedFolders}
                onSearchTermChange={setSearchTerm}
                onSelectPage={handlePageSelection}
                onToggleFolder={handleTreeToggle}
                searchError={searchError}
                searchLoading={searchLoading}
                searchResults={searchResults}
                searchTerm={searchTerm}
                selectedPageId={selectedPageId}
                treeError={treeError}
                treeLoading={treeLoading}
            />
            <section
                className="tw-flex-1 tw-bg-white tw-overflow-y-auto"
            >
                {pageError ? (
                    <div className="tw-mx-2 tw-mb-3 tw-p-3 tw-rounded-[var(--cf-radius-sm)] tw-border tw-border-[color:var(--cf-color-error-border)] tw-bg-[color:var(--cf-color-error-bg)] tw-text-[color:var(--cf-color-error-text)]">
                        {pageError}
                    </div>
                ) : null}
                {pageLoading ? (
                    <div className="tw-flex tw-justify-center tw-items-center tw-h-[76vh]">
                        <div class="loading-overlay-progress-icon">
                            <i className="fa fa-spinner fa-pulse fa-fw tw-text-[40px]" ></i>
                        </div>
                    </div>
                ) : null}

                {!pageLoading && selectedPage ? (
                    <PageArticle
                        contentRef={contentRef}
                        linkCopied={linkCopied}
                        normalizedHtml={normalizedPageHtml}
                        onCopyCurrentUrl={handleCopyCurrentUrl}
                        page={selectedPage}
                    />
                ) : null}

                {!pageLoading && !selectedPage && !pageError ? (
                    <p className="tw-m-0 tw-px-3 tw-py-2 tw-text-[color:var(--cf-color-text-muted)]">
                        Wählen Sie einen Artikel aus, um dessen Inhalt anzuzeigen.
                    </p>
                ) : null}
            </section>
        </div>
    );
};

export default Confluence;
